import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "../../../Common/hooks/useToast";
import { Consultation } from "../../../Common/interfaces/doctor.interfaces";
import { useDoctorContext } from "../../../Navigation/context/DoctorContext";
import {
  fetchConsultation,
  fetchClinicalNotes,
  saveClinicalNotesData,
  uploadNotesFile,
} from "../https/clinical_notes_module.https";

export const PRESET_SYMPTOMS = [
  "Chest Pain",
  "Breathlessness",
  "Sweating",
  "Nausea",
  "Dizziness",
  "Palpitations",
];

export const useClinicalNotesModule = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast, showToast } = useToast();
  const { patientEndedMap } = useDoctorContext();

  useEffect(() => {
    if (id && (patientEndedMap[id] || sessionStorage.getItem(`patientEnded:${id}`) === "true")) {
      showToast("Patient ended the call", "error");
    }
  }, [id, patientEndedMap, showToast]);

  const [consultation, setConsultation] = useState<Consultation | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);

  const [chiefComplaint, setChiefComplaint] = useState("");
  const [duration, setDuration] = useState("");
  const [selectedSymptoms, setSelectedSymptoms] = useState<string[]>([]);
  const [customSymptom, setCustomSymptom] = useState("");
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [patientHistory, setPatientHistory] = useState("");
  const [diagnosis, setDiagnosis] = useState("");
  const [dosAndDonts, setDosAndDonts] = useState("");
  const [hospitalizationRecommended, setHospitalizationRecommended] = useState(false);
  const [ambulanceType, setAmbulanceType] = useState<"ALS" | "BLS" | "None">("None");
  const [additionalNotes, setAdditionalNotes] = useState("");
  const [notesFile, setNotesFile] = useState<File | null>(null);
  const [notesPreview, setNotesPreview] = useState<string | null>(null);


  useEffect(() => {
    if (!id) return;
    fetchConsultation(id)
      .then((r) => { if (r.data?.data) setConsultation(r.data.data); })
      .catch(() => {});
    fetchClinicalNotes(id)
      .then((r) => {
        const notes = r.data?.data;
        if (!notes) return;
        setChiefComplaint(notes.chief_complaint || "");
        setDuration(notes.symptom_duration || "");
        setSelectedSymptoms(notes.symptoms || []);
        setPatientHistory(notes.patient_history || "");
        setDiagnosis(notes.provisional_diagnosis || "");
        setDosAndDonts(notes.dos_and_donts || "");
        setHospitalizationRecommended(notes.hospitalization_recommended || false);
        setAmbulanceType(notes.ambulance_type || "None");
        setAdditionalNotes(notes.additional_notes || "");
      })
      .catch(() => {}); // notes may not exist yet
  }, [id]);

  const handleToggleSymptom = (symptom: string) => {
    setSelectedSymptoms((prev) =>
      prev.includes(symptom) ? prev.filter((s) => s !== symptom) : [...prev, symptom]
    );
  };

  const handleAddCustomSymptom = () => {
    if (!customSymptom.trim()) return;
    if (!selectedSymptoms.includes(customSymptom.trim())) {
      setSelectedSymptoms((prev) => [...prev, customSymptom.trim()]);
    }
    setCustomSymptom("");
    setShowCustomInput(false);
  };

  const handleNotesFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setNotesFile(file);
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (event) => setNotesPreview(event.target?.result as string);
        reader.readAsDataURL(file);
      } else {
        setNotesPreview(null);
      }
    }
    e.target.value = "";
  };

  const handleSave = async () => {
    if (!chiefComplaint.trim()) {
      showToast("Chief Complaint is required", "error");
      return;
    }
    setIsSaving(true);
    let notesFileUrl = "";
    if (notesFile) {
      setIsUploadingFile(true);
      try {
        const uploadRes = await uploadNotesFile(notesFile, consultation?.beneficiary_id || "");
        notesFileUrl = uploadRes.data?.data?.url || "";
      } catch {
        showToast("File upload failed — please retry.", "error");
        setIsUploadingFile(false);
        setIsSaving(false);
        return;
      } finally {
        setIsUploadingFile(false);
      }
    }

    const payload = {
      chief_complaint: chiefComplaint,
      symptom_duration: duration,
      symptoms: selectedSymptoms,
      patient_history: patientHistory,
      provisional_diagnosis: diagnosis,
      dos_and_donts: dosAndDonts,
      hospitalization_recommended: hospitalizationRecommended,
      ambulance_type: ambulanceType,
      additional_notes: additionalNotes,
      notes_file_url: notesFileUrl,
    };
    try {
      await saveClinicalNotesData(id!, payload);
      showToast("Clinical notes saved successfully!", "success");
      const isEnded = patientEndedMap[id!] || sessionStorage.getItem(`patientEnded:${id}`) === "true";
      setTimeout(() => {
        if (isEnded) {
          navigate(`/consultation/${id}/complete`, { state: { patientEnded: true } });
        } else {
          navigate(-1);
        }
      }, 1500);
    } catch {
      showToast("Failed to save clinical notes", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    const isEnded = id && (patientEndedMap[id] || sessionStorage.getItem(`patientEnded:${id}`) === "true");
    if (isEnded) {
      navigate(`/consultation/${id}/complete`, { state: { patientEnded: true } });
    } else {
      navigate(-1);
    }
  };

  return {
    consultation,
    isSaving,
    isUploadingFile,
    chiefComplaint,
    duration,
    selectedSymptoms,
    customSymptom,
    showCustomInput,
    patientHistory,
    diagnosis,
    dosAndDonts,
    hospitalizationRecommended,
    ambulanceType,
    additionalNotes,
    notesFile,
    notesPreview,
    setChiefComplaint,
    setDuration,
    setCustomSymptom,
    setShowCustomInput,
    setPatientHistory,
    setDiagnosis,
    setDosAndDonts,
    setHospitalizationRecommended,
    setAmbulanceType,
    setAdditionalNotes,
    setNotesFile,
    setNotesPreview,
    handleToggleSymptom,
    handleAddCustomSymptom,
    handleNotesFileChange,
    handleSave,
    handleBack,
    navigate,
    toast,
  };
};
